import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import {
  searchPapers,
  fetchPaperAlerts,
  fetchSourceByDoi,
  insertSourceFromPaper,
  insertFolderSource,
  insertPaperAlert,
  updatePaperAlertActive,
  deletePaperAlert,
} from '@/lib/queries/search';
import type {
  SemanticScholarPaper,
  SearchResponse,
  SearchFilters,
  PaperAlert,
} from '@/features/search/types';

// Re-export types for backward compatibility
export type { SemanticScholarPaper, SearchResponse, SearchFilters, PaperAlert };

interface CreateAlertInput {
  query: string;
  criteria_ids?: string[];
  frequency?: 'daily' | 'weekly' | 'monthly';
}

interface ImportPaperInput {
  paper: SemanticScholarPaper;
  folder_id?: string;
}

// =========================================
// Search papers
// =========================================

export function useSearchPapers(filters: SearchFilters | null) {
  return useQuery<SearchResponse>({
    queryKey: ['search-papers', filters],
    queryFn: async () => {
      if (!filters || !filters.query.trim()) {
        return { total: 0, offset: 0, papers: [] };
      }

      const { data, error } = await searchPapers(filters);

      if (error) {
        // Extract specific error message from edge function response
        let message = error.message || 'Search failed';
        try {
          const body = await error.context?.json();
          if (body?.error) message = body.error;
        } catch {
          // Response body may be unavailable
        }
        throw new Error(message);
      }
      return data as SearchResponse;
    },
    enabled: !!filters && !!filters.query.trim(),
    staleTime: 5 * 60 * 1000,
  });
}

// =========================================
// Import paper as source
// =========================================

export function useImportPaper() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paper, folder_id }: ImportPaperInput) => {
      if (!user) throw new Error('Not authenticated');

      // Check if source with same DOI already exists
      if (paper.externalIds?.DOI) {
        const { data: existing } = await fetchSourceByDoi(user.id, paper.externalIds.DOI);

        if (existing) {
          throw new Error('A source with this DOI already exists');
        }
      }

      const { data, error } = await insertSourceFromPaper(user.id, paper);

      if (error) throw error;

      // Also add to folder_sources if folder specified
      if (folder_id && data) {
        await insertFolderSource(folder_id, data.id);
      }

      // Trigger text extraction for open access papers (fire-and-forget)
      if (paper.openAccessPdf && data) {
        api.extractText({ source_id: data.id }).catch((err) => {
          console.error('[search] text extraction failed:', err);
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      toast.success('Paper imported as source');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to import paper');
    },
  });
}

// =========================================
// Paper alerts
// =========================================

export function useAlerts() {
  const user = useAuthStore((s) => s.user);

  return useQuery<PaperAlert[]>({
    queryKey: ['paper-alerts', user?.id],
    queryFn: async () => {
      const { data, error } = await fetchPaperAlerts(user!.id);

      if (error) throw error;
      return data as PaperAlert[];
    },
    enabled: !!user,
  });
}

export function useCreateAlert() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ query, criteria_ids, frequency }: CreateAlertInput) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await insertPaperAlert({
        user_id: user.id,
        query,
        criteria_ids: criteria_ids ?? [],
        frequency: frequency ?? 'weekly',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paper-alerts'] });
      toast.success('Alert created');
    },
    onError: () => {
      toast.error('Failed to create alert');
    },
  });
}

export function useToggleAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await updatePaperAlertActive(id, active);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paper-alerts'] });
    },
  });
}

export function useDeleteAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await deletePaperAlert(id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paper-alerts'] });
      toast.success('Alert deleted');
    },
    onError: () => {
      toast.error('Failed to delete alert');
    },
  });
}
